import React from 'react';
import SittingCard from './SittingCard';

/**
 * ProcedureQueueSection — a titled bucket of SittingCards.
 *
 * Used by ProcedureQueuePage to render the 5 sections (action-pending,
 * ready-to-execute, pending-approval, upcoming-scheduled, delegated)
 * and by the doctor's visit panel for the inline "Procedures" tab.
 *
 * Props:
 *   title        — string (e.g. "Ready to execute")
 *   subtitle?    — optional string hint below the title
 *   sittings     — SittingSummary[] (or SittingResponse[])
 *   viewer       — { memberId, role }
 *   emptyLabel?  — string shown when sittings is empty (default "Nothing here.")
 *   onAction     — forwarded to each SittingCard
 *   dense?       — forwarded
 *   badge?       — ReactNode to render next to the title (e.g. count chip)
 */
function ProcedureQueueSection({
  title,
  subtitle,
  sittings = [],
  viewer,
  emptyLabel = 'Nothing here.',
  onAction,
  dense = false,
  badge,
}) {
  const count = sittings.length;

  return (
    <section className="procedure-queue-section mb-6">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">
            {title}
            {count > 0 && (
              <span className="ml-2 inline-block text-xs font-bold text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-2">
                {count}
              </span>
            )}
            {badge}
          </h3>
          {subtitle && (
            <p className="text-xs text-slate-500 dark:text-slate-500 mt-0.5">{subtitle}</p>
          )}
        </div>
      </div>

      {count === 0 ? (
        <div className="text-sm text-slate-400 italic py-3 px-1">
          {emptyLabel}
        </div>
      ) : (
        <div className="space-y-2">
          {sittings.map((s) => (
            <SittingCard
              key={s.sitting_id}
              sitting={s}
              viewer={viewer}
              onAction={onAction}
              dense={dense}
            />
          ))}
        </div>
      )}
    </section>
  );
}

export default ProcedureQueueSection;
